// api
import { queries } from 'RestAPIs/rapini';
import { DefinitionCount } from 'RestAPIs/tasks/definitions';

// constants
import { tasksGroups } from '../TasksList.const';

// hooks
import { useEffect, useMemo, useRef, useState } from 'react';
import useGetDefinitionsCount from './useGetDefinitionsCount';
import { useQualityMonitorDateRanges } from './useQualityMonitorDateRanges';

// types
import { TaskGroup } from '../TasksList.types';
import { PlanUnitMix } from 'components/types';

// utils
import { getPlanunitCommaIds } from 'components/utils';

type UseTasksDataParams = {
  selectedPlanUnits: PlanUnitMix[];
  tilesOrder: string[];
  hasProcessEndOfEmploymentSettlementAccess: boolean;
  hasFeatureToggleExpenseClaims: boolean;
  hasDayLimitViolationsViewAccess: boolean;
  hasAdditionalCompensationsViewAccess: boolean;
};

const applyOrder = (items: TaskGroup[], orderKeys: string[]): TaskGroup[] => {
  if (!orderKeys.length) return items;
  const byKey = new Map(items.map((item) => [item.key, item]));
  const ordered = orderKeys
    .map((key) => byKey.get(key))
    .filter(Boolean) as TaskGroup[];
  const extras = items.filter((item) => !orderKeys.includes(item.key));
  return [...ordered, ...extras];
};

export function useTasksData({
  selectedPlanUnits,
  tilesOrder,
  hasProcessEndOfEmploymentSettlementAccess,
  hasFeatureToggleExpenseClaims,
  hasDayLimitViolationsViewAccess,
  hasAdditionalCompensationsViewAccess
}: UseTasksDataParams) {
  const [data, setData] = useState<TaskGroup[]>([]);

  const orderRef = useRef(tilesOrder);
  useEffect(() => {
    orderRef.current = tilesOrder;
  }, [tilesOrder]);

  const costCenterIds = getPlanunitCommaIds(selectedPlanUnits);

  const {
    startDateDayLimitViolations,
    endDateDayLimitViolations,
    startDateAdditionalCompensations,
    endDateAdditionalCompensations
  } = useQualityMonitorDateRanges();

  const {
    data: endOfEmploymentSettlementOverviewData,
    isLoading: isLoadingEndOfEmployment
  } = queries.useGetEndOfEmploymentSettlementOverview({
    enabled: !!hasProcessEndOfEmploymentSettlementAccess
  });

  const { data: originalDefinitions = [], isLoading: isLoadingDefinitions } =
    useGetDefinitionsCount(
      { costCenterIds },
      { enabled: selectedPlanUnits.length > 0 }
    );

  const { data: expenseClaimSummaryData, isLoading: isLoadingExpenseClaims } =
    queries.useGetSummary(
      // @ts-ignore
      costCenterIds,
      { enabled: selectedPlanUnits.length > 0 && hasFeatureToggleExpenseClaims }
    );

  const organizationalUnitIds = useMemo(
    () =>
      selectedPlanUnits &&
      Array.from(
        new Set(
          selectedPlanUnits
            .map((unit) =>
              'costCenters' in unit &&
              Array.isArray(unit.costCenters) &&
              unit.costCenters?.length > 0
                ? unit.costCenters.map(({ id }) => id)
                : unit.id
            )
            .flat()
        )
      ).join(','),
    [selectedPlanUnits]
  );

  const {
    data: dayLimitViolationsData,
    isLoading: isLoadingDayLimitViolations
  } = queries.useFindDayLimitViolationsByOrganizationalUnits(
    startDateDayLimitViolations.format('YYYY-MM-DD'),
    endDateDayLimitViolations.format('YYYY-MM-DD'),
    // @ts-ignore
    organizationalUnitIds as string[],
    { enabled: hasDayLimitViolationsViewAccess && !!organizationalUnitIds }
  );

  const {
    data: additionalCompensationsData,
    isLoading: isLoadingAdditionalCompensations
  } = queries.useFindAdditionalCompensationsByOrganizationalUnits(
    startDateAdditionalCompensations.format('YYYY-MM-DD'),
    endDateAdditionalCompensations.format('YYYY-MM-DD'),
    // @ts-ignore
    organizationalUnitIds as string[],
    { enabled: hasAdditionalCompensationsViewAccess && !!organizationalUnitIds }
  );

  useEffect(() => {
    if (
      !selectedPlanUnits ||
      selectedPlanUnits.length === 0 ||
      !originalDefinitions
    ) {
      setData([]);
      return;
    }

    const updateDefinitions: DefinitionCount[] = [
      // TODO: Activate next line when the BE is ready
      // { definitionKey: 'expiringEmploymentContracts', count: 0 },
      ...(hasProcessEndOfEmploymentSettlementAccess
        ? [
            {
              definitionKey: 'endOfEmploymentsSettlements',
              count: endOfEmploymentSettlementOverviewData?.length || 0
            }
          ]
        : []),
      ...(hasDayLimitViolationsViewAccess
        ? [
            {
              definitionKey: 'dayLimitViolations',
              dynamicLabel: 'dayLimitViolationsDynamicLabel',
              date: endDateDayLimitViolations,
              count:
                dayLimitViolationsData?.filter(
                  (d) => d.canBePaidOut || d.isUnjustifiedPayOut
                )?.length || 0
            }
          ]
        : []),
      ...(hasAdditionalCompensationsViewAccess
        ? [
            {
              definitionKey: 'additionalCompensations',
              dynamicLabel: 'additionalCompensationsDynamicLabel',
              count: additionalCompensationsData?.length || 0
            }
          ]
        : [])
    ] as DefinitionCount[];

    const definitions = [...originalDefinitions, ...updateDefinitions];

    const tasks = (Object.keys(tasksGroups) as (keyof typeof tasksGroups)[])
      .map((key) => ({
        key,
        items: tasksGroups[key]
          .map((key) =>
            definitions.find(({ definitionKey }) => definitionKey === key)
          )
          .filter((v): v is DefinitionCount => v !== undefined)
      }))
      .filter(({ items }) => items.length);

    // Travel expense claims task uses different endpoint, so we add it manually
    const travelExpenseClaimsTask = {
      key: 'travelExpenseClaims',
      items: [
        {
          count: expenseClaimSummaryData?.expenseClaimsUnapprovedLastMonth ?? 0,
          definitionKey: 'expenseClaimsUnapprovedLastMonth'
        },
        {
          count:
            expenseClaimSummaryData?.expenseClaimsUnapprovedCurrentMonth ?? 0,
          definitionKey: 'expenseClaimsUnapprovedCurrentMonth'
        }
      ]
    };

    const newData = hasFeatureToggleExpenseClaims
      ? [...tasks, travelExpenseClaimsTask]
      : tasks;

    setData(applyOrder(newData, orderRef.current));
  }, [
    selectedPlanUnits,
    originalDefinitions,
    endOfEmploymentSettlementOverviewData,
    expenseClaimSummaryData,
    dayLimitViolationsData,
    additionalCompensationsData,
    hasProcessEndOfEmploymentSettlementAccess,
    hasDayLimitViolationsViewAccess,
    endDateDayLimitViolations,
    hasAdditionalCompensationsViewAccess,
    hasFeatureToggleExpenseClaims
  ]);

  const isLoading =
    (selectedPlanUnits.length > 0 && isLoadingDefinitions) ||
    (hasProcessEndOfEmploymentSettlementAccess && isLoadingEndOfEmployment) ||
    (selectedPlanUnits.length > 0 &&
      hasFeatureToggleExpenseClaims &&
      isLoadingExpenseClaims) ||
    (hasDayLimitViolationsViewAccess &&
      !!organizationalUnitIds &&
      isLoadingDayLimitViolations) ||
    (hasAdditionalCompensationsViewAccess &&
      !!organizationalUnitIds &&
      isLoadingAdditionalCompensations);

  return {
    data,
    setData,
    isLoading,
    startDateDayLimitViolations,
    endDateDayLimitViolations,
    startDateAdditionalCompensations,
    endDateAdditionalCompensations
  };
}
